import { appFeatures } from "./AppFeatures";
import CloudLogger from "./Logging/CloudLogger";
import LocalLogger from "./Logging/LocalLogger";
import { ILogger } from "./Logging/Logger";
import SharedUtils from "./SharedUtils";

let estimateIdLocalStorage: any = null;
try {
	estimateIdLocalStorage = require("src/utils/asyncLocalStorage").estimateIdLocalStorage;
} catch (_e) {
	console.log("asyncLocalStorage not available, using mock functions");
	estimateIdLocalStorage = {
		getStore: () => {},
	};
}

// Optional Sentry import - only import if package is available
let Sentry: any = null;
let logger: any = null;

try {
	Sentry = require("@sentry/node");
	logger = Sentry.logger;
} catch (e) {
	console.log("Sentry not available, using mock functions");
	// Sentry not available, use mock functions
	Sentry = {
		captureException: () => {},
	};
	logger = {
		warn: () => {},
		info: () => {},
	};
}

// TODO :: maybe refactor this tp use a factory pattern
let _actualLogger: ILogger = new LocalLogger();
export function initializeLogger(isProduction: boolean) {
	if (appFeatures.enableCloudLogging) {
		_actualLogger = new CloudLogger();
	}
}
export function shouldLogToSentry(): boolean {
	return ["production", "staging"].includes(process.env.NODE_ENV ?? "");
}

const errorOnceMap: { [key: string]: boolean } = {};
export function logErrorOnce(...msgs: any[]): void {
	const key = msgs.join();
	if (!errorOnceMap[key]) {
		errorOnceMap[key] = true;
		logError(...msgs);
	}
}

export function logFatalAndExit(...msgs: any[]): void {
	_actualLogger.logCritical(...msgs);
	process.exit(1);
}

export function logCritical(...msgs: any[]): void {
	_actualLogger.logCritical(...msgs);
}
export function logErrorNoStack(...msgs: any[]): void {
	_actualLogger.logErrorNoStack(...msgs);
}

export function logWarningNoStack(...msgs: any[]): void {
	_actualLogger.logWarningNoStack(...msgs);
}

export function logError(...msgs: any[]): void {
	const estimateId = estimateIdLocalStorage.getStore();

	if (shouldLogToSentry()) {
		// Check if first message is an Error object to preserve stack trace; will only print the stack
		// trace if the error object is the first entry in the `msgs` array
		if (msgs.length > 0 && msgs[0] instanceof Error) {
			Sentry.captureException(msgs[0], {
				tags: {
					environment: process.env.NODE_ENV,
					estimateId,
				},
			});
		} else {
			Sentry.captureException(msgs.join(), {
				tags: {
					environment: process.env.NODE_ENV,
					estimateId,
				},
			});
		}
	}
	if (typeof location !== "undefined") {
		logErrorNoStack(...msgs);
		return;
	}
	if (msgs.length > 0 && msgs[0] instanceof Error) {
		const error = msgs[0] as Error;
		_actualLogger.logJSError(error);
	} else {
		_actualLogger.logError(...msgs);
	}
}

export function logWarning(...msgs: any[]): void {
	const estimateId = estimateIdLocalStorage.getStore();

	if (shouldLogToSentry()) {
		logger.warn(msgs.join(), {
			tags: {
				environment: process.env.NODE_ENV,
				estimateId,
			},
		});
	}
	if (typeof location !== "undefined") {
		logWarningNoStack(...msgs);
		return;
	}
	_actualLogger.logWarning(...msgs);
}

export function logInfo(...msgs: any[]): void {
	const estimateId = estimateIdLocalStorage.getStore();

	if (shouldLogToSentry()) {
		logger.info(msgs.join(), {
			tags: {
				environment: process.env.NODE_ENV,
				estimateId,
			},
		});
	}
	_actualLogger.logInfo(...msgs);
}

const debugOnceMap: { [key: string]: boolean } = {};
export function logDebugOnce(...msgs: any[]): void {
	const key = msgs.join();
	if (!debugOnceMap[key]) {
		debugOnceMap[key] = true;
		logDebug(...msgs);
	}
}
export function logDebug(...msgs: any[]): void {
	if (shouldLogToSentry()) {
		logger.info(msgs.join(), {
			tags: {
				environment: process.env.NODE_ENV,
			},
		});
	}
	_actualLogger.logDebug(
		...msgs.map((msg) => {
			try {
				return SharedUtils.clone(msg ?? "");
			} catch (e) {
				return msg;
			}
		}),
	);
}
export function logDebugC(...msgs: any[]): void {
	logDebug(...msgs.map((msg) => JSON.parse(JSON.stringify(msg))));
}
